import { valueOrAlternative } from "utils/namesManipulation";

import { EpcUnitValue } from "../components/address-step/epc-modal/types";

const META_LABELS = new Set(["checked", "none", "selected", "unchecked"]);
const META_KEYS = new Set(["checked", "items", "label", "name", "options", "selected", "text", "value"]);

const isNoneValue = (value: unknown): boolean => {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "" || value.trim().toLowerCase() === "none";
  return false;
};

const isHumanLabel = (value: string): boolean => {
  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 && !META_LABELS.has(normalized);
};

export const extractSelectedCheckboxLabels = (value: unknown): string[] => {
  if (value == null) return [];

  if (typeof value === "string") {
    const trimmed = value.trim();
    return isHumanLabel(trimmed) ? [trimmed] : [];
  }

  if (Array.isArray(value)) {
    const items = value.map((item) => (typeof item === "string" ? item.trim() : item)).filter(Boolean);

    if (items.length >= 2 && typeof items[0] === "boolean") {
      const labels: string[] = [];
      for (let index = 0; index < items.length - 1; index += 2) {
        if (items[index] === true && typeof items[index + 1] === "string" && isHumanLabel(items[index + 1])) {
          labels.push(items[index + 1]);
        }
      }
      return labels;
    }

    const stringItems = items.filter((item): item is string => typeof item === "string");
    if (stringItems.length === items.length) {
      const checkedIndex = stringItems.findIndex((item) => item.toLowerCase() === "checked");
      if (checkedIndex >= 0) {
        const selected = stringItems[checkedIndex + 1];
        return selected && isHumanLabel(selected) ? [selected] : [];
      }
      return stringItems.filter(isHumanLabel);
    }

    return items.flatMap((item) => extractSelectedCheckboxLabels(item));
  }

  if (typeof value === "object") {
    const record = value as Record<string, unknown>;

    if (typeof record.checked === "string" && isHumanLabel(record.checked)) {
      return [record.checked];
    }

    if (record.checked === true) {
      const explicitLabel = record.label ?? record.name ?? record.text;
      if (typeof explicitLabel === "string" && isHumanLabel(explicitLabel)) {
        return [explicitLabel];
      }
    }

    const booleanSelections = Object.entries(record)
      .filter(([key, entry]) => entry === true && isHumanLabel(key) && !META_KEYS.has(key.toLowerCase()))
      .map(([key]) => key);
    if (booleanSelections.length) return booleanSelections;

    return [];
  }

  return [];
};

const formatUnitValue = (entry: undefined | { unit?: null | string; value?: null | number }): null | string => {
  if (!entry || entry.value == null) return null;
  return entry.unit ? `${entry.value} ${entry.unit}` : String(entry.value);
};

export const formatCheckboxOptionStatuses = (items: undefined | unknown[]): string => {
  const fallback = valueOrAlternative("");
  if (!items?.length) return fallback;
  const lines = items
    .map((item) => {
      if (typeof item !== "object" || item == null) return null;
      const { checked, label } = item as { checked?: boolean | null; label?: string };
      if (!label) return null;
      const status = formatYesNoWithFallback(checked, fallback);
      return `${label}: ${status}`;
    })
    .filter(Boolean);
  return lines.length ? lines.join("; ") : fallback;
};

export const formatEpcDisplayValue = (value: unknown, fallback?: string | undefined): string => {
  const fallbackValue = fallback ? fallback : valueOrAlternative("");
  if (value == null || value === "") return fallbackValue;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return isNoneValue(trimmed) ? fallbackValue : trimmed;
  }
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return formatYesNo(value);

  const selected = extractSelectedCheckboxLabels(value);
  if (selected.length) return selected.join(", ");

  if (typeof value === "object" && !Array.isArray(value)) {
    const unitValue = formatUnitValue(value as EpcUnitValue);
    if (unitValue) return unitValue;
  }

  return fallbackValue;
};

export const formatYesNo = (value: boolean | null | undefined): string => {
  if (value == null) return "";
  return value ? "Yes" : "No";
};

export const formatYesNoWithFallback = (value: boolean | null | undefined, fallback?: string | undefined): string => {
  return value === true ? "Yes" : value === false ? "No" : String(fallback);
};
